import { useCallback, useMemo, useState } from "react";
import {
  Modal,
  BlockStack,
  Text,
  InlineStack,
  Banner,
  RadioButton,
  TextField,
  Box,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";

type SeatsOverrideModalProps = {
  open: boolean;
  onClose: () => void;
  spotsMax: number;
  spotsCurrent: number;
  spotsOverride?: number | null;
  onSaved?: (newOverride: number | null) => void;
};

export default function SeatsOverrideModal({
  open,
  onClose,
  spotsMax,
  spotsCurrent,
  spotsOverride,
  onSaved,
}: SeatsOverrideModalProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc, eventId } = useParams<{
    eventOrganizerId: string;
    eventId: string;
  }>();
  const { PUT } = useApiRequest();

  const [mode, setMode] = useState<"auto" | "manual">(
    spotsOverride == null ? "auto" : "manual"
  );
  const [overrideValue, setOverrideValue] = useState<string>(
    spotsOverride != null ? String(spotsOverride) : ""
  );
  // Mutation for saving seats override
  const saveSeatsOverride = useMutation({
    mutationFn: (body: { spotsOverride: number | null }) =>
      PUT(
        `/api/healthcare-companies/${hcc}/e/events/${eventId}/seats-override`,
        body
      ),
    onSuccess: (_data, variables) => {
      onSaved?.(variables.spotsOverride);
      onClose();
    },
  });
  const saving = saveSeatsOverride.isPending;

  const lastSeatsThreshold = useMemo(
    () => Math.ceil(spotsMax * 0.8),
    [spotsMax]
  );

  const isManualInvalid = useMemo(() => {
    if (mode !== "manual") return false;
    if (overrideValue === "") return true;
    const n = Number(overrideValue);
    return Number.isNaN(n) || n < 0 || n > spotsMax;
  }, [mode, overrideValue, spotsMax]);

  const handleSave = useCallback(() => {
    if (!hcc || !eventId) return;
    const body = {
      spotsOverride:
        mode === "auto"
          ? null
          : Math.max(0, Math.min(spotsMax, Number(overrideValue))),
    } as { spotsOverride: number | null };
    if (mode === "manual" && isManualInvalid) return;
    saveSeatsOverride.mutate(body);
  }, [
    hcc,
    eventId,
    mode,
    overrideValue,
    spotsMax,
    saveSeatsOverride,
    isManualInvalid,
  ]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("seatsOverride.title")}
      primaryAction={{
        content: t("common.save"),
        onAction: handleSave,
        loading: saving,
        disabled: saving || isManualInvalid,
      }}
      secondaryActions={[
        { content: t("common.cancel"), onAction: onClose, disabled: saving },
      ]}
    >
      <Modal.Section>
        {mode === "manual" && (
          <Banner tone="warning">{t("seatsOverride.bannerManual")}</Banner>
        )}

        <Box paddingBlockStart="100" paddingBlockEnd="100">
          <ReactMarkdown components={{
                p: ({ children }) => (
                  <Text as="p" tone="subdued">
                    {children}
                  </Text>
                ),
              }}>
                {t("seatsOverride.description")}
          </ReactMarkdown>
        </Box>

        <BlockStack gap="400">
          <BlockStack gap="200">
            <div onClick={() => setMode("auto")} style={{ cursor: "pointer" }}>
              <Box
                borderWidth="025"
                borderColor="border"
                borderRadius="200"
                padding="200"
                background="bg-surface"
              >
                <RadioButton
                  label={t("seatsOverride.auto")}
                  helpText={t("seatsOverride.autoHelp")}
                  checked={mode === "auto"}
                  id="autoSeats"
                  name="seats-mode"
                  onChange={() => setMode("auto")}
                />
              </Box>
            </div>
            <div
              onClick={() => setMode("manual")}
              style={{ cursor: "pointer" }}
            >
              <Box
                borderWidth="025"
                borderColor="border"
                borderRadius="200"
                padding="200"
                background="bg-surface"
              >
                <RadioButton
                  label={t("seatsOverride.manual")}
                  helpText={t("seatsOverride.manualHelp")}
                  checked={mode === "manual"}
                  id="manualSeats"
                  name="seats-mode"
                  onChange={() => setMode("manual")}
                />
              </Box>
            </div>
          </BlockStack>
          {mode === "manual" && (
            <>
              <BlockStack gap="150">
                <Text as="p" tone="subdued">
                  {t("seatsOverride.eventInfo")}
                </Text>
                <BlockStack gap="150">
                  <InlineStack gap="200" align="start" blockAlign="start">
                    <Text as="span">•</Text>
                    <Text as="p">
                      {t("seatsOverride.attendees")}{" "}
                      <Text as="span" fontWeight="semibold">
                        {spotsCurrent ?? 0}
                      </Text>
                    </Text>
                  </InlineStack>
                  <InlineStack gap="200" align="start" blockAlign="start">
                    <Text as="span">•</Text>
                    <Text as="p">
                      {t("seatsOverride.lastSeatsAt")}{" "}
                      <Text as="span" fontWeight="semibold">
                        {lastSeatsThreshold ?? 0}
                      </Text>{" "}
                      {t("seatsOverride.orMore")}
                    </Text>
                  </InlineStack>
                  <InlineStack gap="200" align="start" blockAlign="start">
                    <Text as="span">•</Text>
                    <Text as="p">
                      {t("seatsOverride.soldOutAt")}{" "}
                      <Text as="span" fontWeight="semibold">
                        {spotsMax ?? 0}
                      </Text>
                    </Text>
                  </InlineStack>
                  <InlineStack gap="200" align="start" blockAlign="start">
                    <Text as="span">•</Text>
                    <Text as="p">
                      {t("seatsOverride.maxAllowed")}{" "}
                      <Text as="span" fontWeight="semibold">
                        {spotsMax ?? 0}
                      </Text>
                    </Text>
                  </InlineStack>
                </BlockStack>
              </BlockStack>

              <BlockStack gap="150">
                <Text as="p">{t("seatsOverride.overrideLabel")}</Text>
                <InlineStack gap="200" align="start" blockAlign="center">
                  <Box minWidth="120px">
                    <TextField
                      label=""
                      type="number"
                      min={0}
                      max={spotsMax}
                      value={mode === "manual" ? overrideValue : ""}
                      onChange={setOverrideValue}
                      disabled={mode !== "manual"}
                      autoComplete="off"
                    />
                  </Box>
                  {mode === "manual" && (
                    <Text as="span" tone="subdued">
                      {t("seatsOverride.ofMax", { max: spotsMax })}
                    </Text>
                  )}
                </InlineStack>
                <Text as="p" tone="subdued">
                  {t("seatsOverride.helperText")}
                </Text>
              </BlockStack>
            </>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
